import {
  blank,
  box,
  fromJsonNode,
  lines,
  table,
  vstack,
  type Column,
  type JsonNode,
  type LayoutFn,
  type Line,
  type Segment,
  type Theme,
} from "../termui";
import { dark } from "../theme";
import { isNumericValue, renderPrefixedText, wrapText } from "./common";

const JSON_NODE_TYPES = new Set([
  "vstack",
  "hstack",
  "box",
  "lines",
  "separator",
  "blank",
  "table",
  "statusbar",
  "tabs",
  "bar",
  "text",
] as const satisfies readonly string[]);

function isJsonNode(value: unknown): value is JsonNode {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const type = (value as { type?: unknown }).type;
  return typeof type === "string" && JSON_NODE_TYPES.has(type as typeof JSON_NODE_TYPES extends Set<infer T> ? T : never);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function primitiveStyle(
  value: unknown,
  theme: Theme,
): Omit<Exclude<Segment, string>, "text" | "onClick"> | undefined {
  if (typeof value === "string") return { color: theme.green };
  if (typeof value === "number") return { color: theme.yellow };
  if (typeof value === "boolean") {
    return { color: value ? theme.green : theme.red, bold: true };
  }
  if (value === null) return { color: theme.brightBlack, dim: true };
  return undefined;
}

function primitiveText(value: unknown): string {
  if (value === null) return "null";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (value === undefined) return "undefined";
  return String(value);
}

function isPrimitiveJsonValue(value: unknown): boolean {
  return (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

function isPrimitiveArrayValue(value: unknown): value is unknown[] {
  return Array.isArray(value) && value.every((item) => isPrimitiveJsonValue(item));
}

function segmentLength(segment: Segment): number {
  return typeof segment === "string" ? segment.length : segment.text.length;
}

function lineLength(line: Line): number {
  let total = 0;
  for (const segment of line) total += segmentLength(segment);
  return total;
}

function primitiveSegment(value: unknown, theme: Theme): Segment {
  const text = primitiveText(value);
  const style = primitiveStyle(value, theme);
  return style ? { text, ...style } : text;
}

function indentLayout(layout: LayoutFn, indent: number): LayoutFn {
  return (w) => {
    if (indent <= 0) return layout(w);
    const pad = " ".repeat(indent);
    if (w <= indent) return layout(w);
    return layout(w - indent).map((line) => [pad, ...line]);
  };
}

function sectionHeader(title: string, theme: Theme): LayoutFn {
  return (w) => {
    const label = ` ${title} `;
    const remaining = Math.max(0, w - label.length);
    return [[
      { text: label, color: theme.brightBlack, bold: true },
      { text: "─".repeat(remaining), color: theme.border },
    ]];
  };
}

function isShallowSectionValue(value: unknown): boolean {
  if (isRecord(value)) {
    const vals = Object.values(value);
    return vals.every((item) => isPrimitiveJsonValue(item) || isPrimitiveArrayValue(item));
  }
  if (Array.isArray(value)) return value.every((item) => isPrimitiveJsonValue(item));
  return isPrimitiveJsonValue(value);
}

function stackWithSpacing(children: LayoutFn[]): LayoutFn {
  if (children.length === 0) return blank();
  if (children.length === 1) return children[0]!;

  const layouts: LayoutFn[] = [];
  children.forEach((child, index) => {
    if (index > 0) layouts.push(blank());
    layouts.push(child);
  });
  return vstack(...layouts);
}

function renderPrimitiveValue(value: unknown, theme: Theme): LayoutFn {
  return (w) => {
    const text = primitiveText(value);
    const style = primitiveStyle(value, theme);
    const rows = text.split("\n");
    const out: Line[] = [];

    for (const row of rows) {
      const wrapped = wrapText(row, w);
      for (const chunk of wrapped) {
        out.push([style ? { text: chunk, ...style } : chunk]);
      }
    }

    return out.length > 0 ? out : [[{ text: " ", dim: true }]];
  };
}

function renderStandalonePrimitiveArray(value: unknown[], theme: Theme): LayoutFn {
  return (w) => {
    if (value.length === 0) {
      return [[{ text: "(empty array)", color: theme.brightBlack, dim: true }]];
    }

    const out: Line[] = [];
    const digits = String(value.length).length;
    value.forEach((item, index) => {
      const prefix = ` ${String(index + 1).padStart(digits, " ")}. `;
      out.push(
        ...renderPrefixedText(
          primitiveText(item),
          w,
          { text: prefix, color: theme.brightBlack },
          " ".repeat(prefix.length),
          primitiveStyle(item, theme),
        ),
      );
    });
    return out;
  };
}

function isTabularRecordArray(value: unknown[]): value is Record<string, unknown>[] {
  if (value.length === 0) return false;
  if (!value.every((item) => isRecord(item))) return false;

  const keys = Object.keys(value[0]!);
  if (keys.length < 2) return false;

  return value.every((row) => {
    const rowKeys = Object.keys(row);
    if (rowKeys.length !== keys.length) return false;
    if (!keys.every((key) => rowKeys.includes(key))) return false;
    return keys.every((key) => isPrimitiveJsonValue(row[key]));
  });
}

function renderTabularRecordArray(
  rows: Record<string, unknown>[],
  theme: Theme,
): LayoutFn {
  const keys = Object.keys(rows[0] ?? {});

  const columns: Column[] = keys.map((key) => {
    const values = rows.map((row) => row[key]);
    const isBoolCol = values.every((v) => typeof v === "boolean");
    const isNumCol = values.every((v) => isNumericValue(v));

    return {
      key,
      title: key,
      align: isNumCol ? "right" : "left",
      headerColor: theme.cyan,
      color: isBoolCol
        ? (value: unknown) =>
            value === true ? theme.green : value === false ? theme.red : undefined
        : isNumCol
          ? theme.yellow
          : undefined,
    };
  });

  return table(columns, rows, {
    borderColor: theme.border,
  });
}

function renderArrayValue(value: unknown[], theme: Theme): LayoutFn {
  if (value.length === 0) {
    return lines([[{ text: "(empty array)", color: theme.brightBlack, dim: true }]]);
  }

  if (isTabularRecordArray(value)) {
    return renderTabularRecordArray(value, theme);
  }

  if (value.every((item) => isPrimitiveJsonValue(item))) {
    return renderStandalonePrimitiveArray(value, theme);
  }

  const children = value.map((item, index) =>
    renderJsonSection(`item ${index + 1}`, item, theme),
  );
  return stackWithSpacing(children);
}

type PrimitiveRow =
  | { kind: "primitive"; key: string; value: unknown }
  | { kind: "array"; key: string; value: unknown[] };

function renderPrimitiveRows(rows: PrimitiveRow[], theme: Theme): LayoutFn {
  return (w) => {
    const out: Line[] = [];
    const keyWidth = Math.max(...rows.map((row) => row.key.length), 0);

    for (const row of rows) {
      const keyPrefix = ` ${row.key.padEnd(keyWidth)}: `;

      if (row.kind === "primitive") {
        out.push(
          ...renderPrefixedText(
            primitiveText(row.value),
            w,
            { text: keyPrefix, color: theme.brightBlack },
            " ".repeat(keyPrefix.length),
            primitiveStyle(row.value, theme),
          ),
        );
        continue;
      }

      if (row.value.length === 0) {
        out.push([
          { text: keyPrefix, color: theme.brightBlack },
          { text: "(empty)", color: theme.brightBlack, dim: true },
        ]);
        continue;
      }

      const inlineSegments: Segment[] = [];
      row.value.forEach((item, index) => {
        if (index > 0) inlineSegments.push({ text: ", ", color: theme.brightBlack });
        inlineSegments.push(primitiveSegment(item, theme));
      });
      const inlineLine: Line = [{ text: keyPrefix, color: theme.brightBlack }, ...inlineSegments];

      if (lineLength(inlineLine) <= w) {
        out.push(inlineLine);
        continue;
      }

      out.push([{ text: ` ${row.key}:`, color: theme.brightBlack }]);
      const digits = String(row.value.length).length;
      row.value.forEach((item, index) => {
        const listPrefix = `   ${String(index + 1).padStart(digits, " ")}. `;
        out.push(
          ...renderPrefixedText(
            primitiveText(item),
            w,
            { text: listPrefix, color: theme.brightBlack },
            " ".repeat(listPrefix.length),
            primitiveStyle(item, theme),
          ),
        );
      });
    }

    return out;
  };
}

function renderObjectValue(value: Record<string, unknown>, theme: Theme): LayoutFn {
  const entries = Object.entries(value);
  if (entries.length === 0) {
    return lines([[{ text: "(empty object)", color: theme.brightBlack, dim: true }]]);
  }

  const groups: LayoutFn[] = [];
  let pendingRows: PrimitiveRow[] = [];
  const flushRows = () => {
    if (pendingRows.length === 0) return;
    groups.push(renderPrimitiveRows(pendingRows, theme));
    pendingRows = [];
  };

  for (const [key, item] of entries) {
    if (isPrimitiveJsonValue(item)) {
      pendingRows.push({ kind: "primitive", key, value: item });
      continue;
    }

    if (isPrimitiveArrayValue(item)) {
      pendingRows.push({ kind: "array", key, value: item });
      continue;
    }

    flushRows();
    groups.push(renderJsonSection(key, item, theme));
  }

  flushRows();

  return stackWithSpacing(groups);
}

function renderJsonContent(value: unknown, theme: Theme): LayoutFn {
  if (isRecord(value)) return renderObjectValue(value, theme);
  if (Array.isArray(value)) return renderArrayValue(value, theme);
  return renderPrimitiveValue(value, theme);
}

function renderJsonSection(title: string, value: unknown, theme: Theme): LayoutFn {
  if (Array.isArray(value) && isTabularRecordArray(value)) {
    return vstack(sectionHeader(title, theme), indentLayout(renderJsonContent(value, theme), 1));
  }

  if (isShallowSectionValue(value)) {
    return vstack(sectionHeader(title, theme), indentLayout(renderJsonContent(value, theme), 1));
  }

  return box(renderJsonContent(value, theme), {
    title,
    borderColor: theme.border,
  });
}

export function fromJson(
  value: unknown,
  opts?: { theme?: Theme; title?: string },
): LayoutFn {
  const theme = opts?.theme ?? dark;
  let parsedValue = value;

  if (typeof value === "string") {
    try {
      parsedValue = JSON.parse(value);
    } catch {
      parsedValue = value;
    }
  }

  if (isJsonNode(parsedValue)) return fromJsonNode(parsedValue);

  const content = renderJsonContent(parsedValue, theme);
  if (opts?.title) {
    return box(content, {
      title: opts.title,
      borderColor: theme.border,
    });
  }
  return content;
}
